import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { StatCard } from "@/components/StatCard";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader } from "@/components/Loading";
import { Progress } from "@/components/ui/progress";
import {
  BookOpen, Users, GraduationCap, UserCheck, Clock, Award, Bell, CalendarClock,
  CheckCircle2, TrendingUp, Layers, Trophy, Target, Star, Medal, Megaphone, ArrowLeft, CheckCheck
} from "lucide-react";

const BADGE_ICON = { award: Award, trophy: Trophy, target: Target, clock: Clock, star: Star, graduation: GraduationCap, check: CheckCircle2 };

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => { api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => setStats({})); }, []);

  if (!stats) return <PageLoader />;

  if (user.role === "admin" || user.role === "supervisor") return <AdminDash stats={stats} nav={nav} />;
  if (user.role === "teacher") return <TeacherDash stats={stats} />;
  return <StudentDash stats={stats} nav={nav} />;
}

function AdminDash({ stats, nav }) {
  return (
    <>
      <PageHeader title="لوحة التحكم" subtitle="نظرة عامة على البرامج العلمية في مدينة حلب" />
      <div className="stagger grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard testid="stat-active" icon={CheckCircle2} label="برامج نشطة" value={stats.programs_active} accent="green" />
        <StatCard testid="stat-upcoming" icon={CalendarClock} label="برامج قادمة" value={stats.programs_upcoming} accent="blue" />
        <StatCard testid="stat-finished" icon={Layers} label="برامج منتهية" value={stats.programs_finished} accent="navy" />
        <StatCard testid="stat-students" icon={Users} label="طلاب مسجلون" value={stats.students} accent="gold" />
        <StatCard testid="stat-lectures" icon={GraduationCap} label="المحاضرات" value={stats.lectures} accent="navy" />
        <StatCard testid="stat-teachers" icon={Users} label="المدرسون" value={stats.teachers} accent="green" />
        <StatCard testid="stat-enrollments" icon={UserCheck} label="تسجيلات مقبولة" value={stats.enrollments} accent="blue" />
        <StatCard testid="stat-pending" icon={Bell} label="تسجيلات معلّقة" value={stats.pending_enrollments} accent="red" />
      </div>

      <div className="mt-8 rounded-2xl border border-slate-100 bg-white p-6 soft-shadow">
        <h3 className="mb-4 font-heading text-lg font-bold text-navy">برامج ستبدأ قريبًا</h3>
        {(!stats.starting_soon || stats.starting_soon.length === 0) ? (
          <p className="py-8 text-center text-sm text-slate-400">لا توجد برامج قادمة حاليًا</p>
        ) : (
          <div className="space-y-3">
            {stats.starting_soon.map((p) => (
              <button key={p.id} onClick={() => nav(`/app/programs/${p.id}`)} data-testid={`soon-${p.id}`}
                className="flex w-full items-center justify-between rounded-xl border border-slate-100 p-4 text-start transition-colors hover:bg-slate-50">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-navy/5 text-navy"><BookOpen className="h-5 w-5" /></div>
                  <div>
                    <p className="font-semibold text-navy">{p.name}</p>
                    <p className="text-xs text-slate-400">يبدأ في {p.start_date}</p>
                  </div>
                </div>
                <span className="text-xs font-semibold text-greenx">{p.enrolled_count} مسجل</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function TeacherDash({ stats }) {
  return (
    <>
      <PageHeader title="لوحة التحكم" subtitle="متابعة برامجك ومحاضراتك" />
      <div className="stagger grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard testid="stat-programs" icon={BookOpen} label="برامجي" value={stats.programs} accent="navy" />
        <StatCard testid="stat-lectures" icon={GraduationCap} label="المحاضرات" value={stats.lectures} accent="green" />
        <StatCard testid="stat-students" icon={Users} label="الطلاب" value={stats.students} accent="gold" />
        <StatCard testid="stat-hours" icon={Clock} label="ساعات التدريس" value={stats.teaching_hours} suffix="ساعة" accent="blue" />
      </div>
    </>
  );
}

function StudentDash({ stats, nav }) {
  const rank = stats.rank || {};
  const earned = (stats.badges || []).filter((b) => b.earned);
  const nextTh = stats.next_threshold;
  const levelPct = nextTh ? Math.min(100, Math.round((stats.points / nextTh) * 100)) : 100;
  const [rsvped, setRsvped] = useState(stats.next_lecture?.rsvped);

  const rsvp = async () => {
    try { await api.post(`/lectures/${stats.next_lecture.id}/rsvp`); setRsvped(true); toast.success("تم تأكيد نيتك للحضور"); }
    catch (e) { toast.error("تعذّر التأكيد"); }
  };

  return (
    <>
      <PageHeader title="لوحتي" subtitle="متابعة برامجك وحضورك وإنجازك العلمي" />

      {/* Latest announcement banner */}
      {stats.latest_announcement && (
        <Link to="/app/announcements" data-testid="latest-ann-banner" className="mb-6 block overflow-hidden rounded-2xl border border-gold/30 bg-gradient-to-l from-gold/10 to-transparent p-5 transition-colors hover:from-gold/20">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gold/20 text-gold-dark"><Megaphone className="h-5 w-5" /></div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-white px-2 py-0.5 text-xs text-slate-500">{stats.latest_announcement.program_name}</span>
                {stats.latest_announcement.is_my_unit && <span className="rounded-full bg-gold/20 px-2 py-0.5 text-xs font-semibold text-gold-dark">يخص وحدتك</span>}
              </div>
              <h3 className="mt-1.5 font-heading text-lg font-bold text-navy">{stats.latest_announcement.title}</h3>
              <p className="mt-1 line-clamp-1 text-sm text-slate-500">{stats.latest_announcement.body}</p>
              <span className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-greenx">انقر لعرض المزيد <ArrowLeft className="h-4 w-4" /></span>
            </div>
          </div>
        </Link>
      )}

      {/* Level + rank hero */}
      <div className="mb-6 grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl bg-navy p-6 text-white soft-shadow lg:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-300">مستوى الإنجاز</p>
              <p className="font-heading text-3xl font-extrabold text-gold">{stats.level_title}</p>
            </div>
            <div className="text-left">
              <p className="font-heading text-4xl font-extrabold" data-testid="student-points">{stats.points}</p>
              <p className="text-sm text-slate-300">نقطة</p>
            </div>
          </div>
          {nextTh && (
            <div className="mt-5">
              <div className="mb-1 flex justify-between text-xs text-slate-300"><span>التقدم للمستوى التالي</span><span>{stats.points} / {nextTh}</span></div>
              <div className="h-2.5 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-gold transition-all" style={{ width: `${levelPct}%` }} /></div>
            </div>
          )}
        </div>
        <button onClick={() => nav("/app/leaderboard")} data-testid="rank-card" className="rounded-2xl border border-slate-100 bg-card p-6 text-start soft-shadow card-hover">
          <div className="flex items-center gap-2 text-gold-dark"><Trophy className="h-5 w-5" /><span className="text-sm text-slate-500">ترتيبك في وحدتك</span></div>
          <p className="mt-3 font-heading text-4xl font-extrabold text-navy">{rank.position || "—"} <span className="text-lg text-slate-400">من {rank.total || 0}</span></p>
          <p className="mt-1 text-xs text-slate-400">{rank.unit_name ? `الوحدة: ${rank.unit_name}` : "لم تُحدَّد وحدتك السكنية"}</p>
        </button>
      </div>

      <div className="stagger grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard testid="stat-programs" icon={BookOpen} label="برامجي" value={stats.programs} accent="navy" />
        <StatCard testid="stat-attendance" icon={TrendingUp} label="نسبة الحضور" value={stats.attendance_percent} suffix="%" accent="green" />
        <StatCard testid="stat-hours" icon={Clock} label="الساعات العلمية" value={stats.attended_hours} suffix={`/ ${stats.total_hours}`} accent="gold" />
        <StatCard testid="stat-completed" icon={Award} label="برامج مكتملة" value={stats.completed_programs} accent="purple" />
        <StatCard testid="stat-lectures" icon={GraduationCap} label="محاضرات حضرتها" value={stats.lectures_attended} accent="blue" />
        <StatCard testid="stat-assignments" icon={CheckCheck} label="واجبات مسلّمة" value={stats.assignments} accent="green" />
      </div>

      {/* Badges */}
      <div className="mt-6 rounded-2xl border border-slate-100 bg-card p-6 soft-shadow">
        <h3 className="mb-4 font-heading text-lg font-bold text-navy">شارات الإنجاز {earned.length > 0 && <span className="text-sm text-gold-dark">({earned.length})</span>}</h3>
        <div className="flex flex-wrap gap-3" data-testid="badges">
          {(stats.badges || []).map((b) => { const I = BADGE_ICON[b.icon] || Award; return (
            <div key={b.key} className={`flex items-center gap-2 rounded-xl border px-4 py-2 transition-transform hover:scale-105 ${b.earned ? "border-gold/40 bg-gold/10 text-gold-dark" : "border-slate-100 bg-slate-50 text-slate-300"}`}>
              <I className="h-5 w-5" /> <span className="text-sm font-semibold">{b.label}</span>
            </div>
          ); })}
        </div>
      </div>

      {/* Next lecture */}
      <div className="mt-6 rounded-2xl border border-slate-100 bg-card p-6 soft-shadow">
        <h3 className="mb-4 font-heading text-lg font-bold text-navy">المحاضرة القادمة</h3>
        {stats.next_lecture ? (
          <div className="rounded-xl bg-navy p-5 text-white">
            <p className="font-heading text-lg font-bold">{stats.next_lecture.title}</p>
            <p className="mt-1 text-sm text-slate-300">{stats.next_lecture.program_name}</p>
            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-slate-200">
              <span className="flex items-center gap-1"><CalendarClock className="h-4 w-4" /> {stats.next_lecture.date}</span>
              <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {stats.next_lecture.start_time}</span>
            </div>
            <div className="mt-4 flex gap-3">
              {rsvped ? (
                <span data-testid="rsvp-done" className="inline-flex items-center gap-1 rounded-xl bg-emerald-500/20 px-4 py-2 text-sm font-semibold text-emerald-100"><CheckCircle2 className="h-4 w-4" /> أكّدت حضورك</span>
              ) : (
                <button onClick={rsvp} data-testid="rsvp-btn" className="rounded-xl bg-gold px-5 py-2 text-sm font-bold text-navy transition-transform hover:scale-105 active:scale-95">موافق، سأحضر</button>
              )}
              <button onClick={() => nav("/app/attendance")} data-testid="go-attendance" className="rounded-xl bg-white/10 px-5 py-2 text-sm font-semibold text-white hover:bg-white/20">تسجيل الحضور (QR)</button>
            </div>
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-slate-400">لا توجد محاضرات قادمة</p>
        )}
      </div>
    </>
  );
}
